import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import QRCode from "qrcode";
import { SheqPpeRecord } from "../../../redux/sheqApi";

const NAVY_R = 18;
const NAVY_G = 52;
const NAVY_B = 86;

export interface ReturnSlipOptions {
  record: SheqPpeRecord;
  signatureDataUrl?: string;
  receivingOfficerName?: string;
  logoDataUrl?: string;
}

export async function generateReturnSlip(
  opts: ReturnSlipOptions,
): Promise<void> {
  const { record, signatureDataUrl, receivingOfficerName, logoDataUrl } = opts;

  const printDate = new Date().toLocaleDateString("en-ZA", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 14;
  let y = margin;

  // ── Header ────────────────────────────────────────────────────────────
  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, "PNG", margin, y, 22, 22);
    } catch {
      /* optional */
    }
  }

  const qrTarget = `${window.location.origin}/coms/sheq?workerId=${encodeURIComponent(record.workerId)}`;
  let qrDataUrl: string | undefined;
  try {
    qrDataUrl = await QRCode.toDataURL(qrTarget, { width: 80, margin: 1 });
  } catch {
    /* optional */
  }
  if (qrDataUrl) {
    doc.addImage(qrDataUrl, "PNG", pageW - margin - 22, y, 22, 22);
  }

  doc.setFontSize(15);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(NAVY_R, NAVY_G, NAVY_B);
  doc.text("PPE RETURN SLIP", pageW / 2, y + 8, { align: "center" });

  doc.setFontSize(8.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(90, 90, 90);
  doc.text("WestProp VMS · Health & Safety Management", pageW / 2, y + 15, {
    align: "center",
  });
  if (record.blockName) {
    doc.text(`Site / Block: ${record.blockName}`, pageW / 2, y + 21, {
      align: "center",
    });
  }

  y += 29;

  doc.setDrawColor(NAVY_R, NAVY_G, NAVY_B);
  doc.setLineWidth(0.6);
  doc.line(margin, y, pageW - margin, y);
  y += 6;

  // ── Worker info box ────────────────────────────────────────────────────
  const boxH = record.department ? 20 : 16;
  doc.setFillColor(236, 242, 248);
  doc.roundedRect(margin, y, pageW - 2 * margin, boxH, 2, 2, "F");

  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(NAVY_R, NAVY_G, NAVY_B);
  doc.text("WORKER INFORMATION", margin + 3, y + 5);

  doc.setFont("helvetica", "normal");
  doc.setTextColor(30, 30, 30);
  doc.setFontSize(9);

  const line2Y = y + 12;
  doc.text(`Name:  ${record.workerName}`, margin + 3, line2Y);
  if (record.workerEmployeeNo) {
    doc.text(`Emp. No:  ${record.workerEmployeeNo}`, margin + 75, line2Y);
  }
  if (record.department) {
    doc.text(`Department:  ${record.department}`, margin + 3, y + boxH - 3);
  }

  y += boxH + 7;

  // ── PPE return detail table ────────────────────────────────────────────
  doc.setFontSize(8.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(NAVY_R, NAVY_G, NAVY_B);
  doc.text("PPE RETURNED", margin, y);
  y += 3;

  const statusColors: Record<string, [number, number, number]> = {
    RETURNED: [21, 101, 192],
    DAMAGED: [198, 40, 40],
    LOST: [230, 81, 0],
    EXPIRED: [109, 76, 65],
  };
  const statusColor = statusColors[record.status] ?? [80, 80, 80];

  autoTable(doc, {
    startY: y,
    head: [
      [
        "Item",
        "Code",
        "Size",
        "Qty",
        "Batch / Serial No.",
        "Issue Date",
        "Return Date",
        "Condition",
        "Notes",
      ],
    ],
    body: [
      [
        record.ppeName,
        record.ppeCode ?? "—",
        record.size ?? "—",
        String(record.quantity),
        record.batchOrSerialNumber ?? "—",
        record.issuedDate,
        record.returnDate ?? "—",
        record.returnCondition ?? record.condition,
        record.returnNotes ?? "—",
      ],
    ],
    styles: { fontSize: 8, cellPadding: 2.5 },
    headStyles: {
      fillColor: [NAVY_R, NAVY_G, NAVY_B],
      textColor: 255,
      fontStyle: "bold",
      fontSize: 8,
    },
    columnStyles: {
      0: { cellWidth: 40 },
      1: { cellWidth: 13 },
      2: { cellWidth: 11 },
      3: { cellWidth: 9 },
      4: { cellWidth: 30 },
      5: { cellWidth: 21 },
      6: { cellWidth: 21 },
      7: { cellWidth: 18 },
      8: { cellWidth: "auto" as unknown as number },
    },
    margin: { left: margin, right: margin },
    theme: "grid",
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  y = (doc as any).lastAutoTable.finalY + 5;

  // ── Status badge ──────────────────────────────────────────────────────
  doc.setFillColor(...statusColor);
  doc.roundedRect(margin, y, 32, 7, 1.5, 1.5, "F");
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text(record.status, margin + 16, y + 5, { align: "center" });
  y += 13;

  // ── Declaration ────────────────────────────────────────────────────────
  const declText =
    '"I confirm that the above personal protective equipment has been returned in the ' +
    "stated condition. Any damage, defects or loss have been noted above.\"";
  const declLines = doc.splitTextToSize(declText, pageW - 2 * margin - 8);
  const declH = declLines.length * 5 + 12;

  doc.setFillColor(232, 245, 233);
  doc.setDrawColor(46, 125, 50);
  doc.setLineWidth(0.3);
  doc.roundedRect(margin, y, pageW - 2 * margin, declH, 2, 2, "FD");

  doc.setFontSize(8);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(27, 94, 32);
  doc.text(declLines, margin + 4, y + 6);
  y += declH + 8;

  // ── Signature section ──────────────────────────────────────────────────
  const colW = (pageW - 2 * margin - 6) / 2;
  const sigH = 34;

  // Staff / returnee signature
  doc.setDrawColor(160, 160, 160);
  doc.setLineWidth(0.3);
  doc.rect(margin, y, colW, sigH);

  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(NAVY_R, NAVY_G, NAVY_B);
  doc.text("Staff Signature (returnee)", margin + 2, y + 5);

  if (signatureDataUrl) {
    try {
      doc.addImage(signatureDataUrl, "PNG", margin + 2, y + 7, colW - 4, 20);
    } catch {
      /* blank area */
    }
  } else {
    doc.setDrawColor(190, 190, 190);
    doc.setLineWidth(0.4);
    doc.line(margin + 4, y + 24, margin + colW - 4, y + 24);
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(60, 60, 60);
  doc.text(`Print Name: ${record.workerName}`, margin + 2, y + sigH - 5);
  doc.text(`Date: ${record.returnDate ?? printDate}`, margin + colW / 2, y + sigH - 5);

  // Receiving officer box
  const officerX = margin + colW + 6;
  doc.setDrawColor(160, 160, 160);
  doc.setLineWidth(0.3);
  doc.rect(officerX, y, colW, sigH);

  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(NAVY_R, NAVY_G, NAVY_B);
  doc.text("Receiving Officer Signature", officerX + 2, y + 5);

  doc.setDrawColor(190, 190, 190);
  doc.setLineWidth(0.4);
  doc.line(officerX + 4, y + 24, officerX + colW - 4, y + 24);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(60, 60, 60);
  const officerDisplay =
    receivingOfficerName ?? record.issuedByName ?? "____________________";
  doc.text(`Print Name: ${officerDisplay}`, officerX + 2, y + sigH - 5);
  doc.text(`Date: ${record.returnDate ?? printDate}`, officerX + colW / 2, y + sigH - 5);

  y += sigH + 8;

  // ── Footer ─────────────────────────────────────────────────────────────
  doc.setLineWidth(0.3);
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, y - 3, pageW - margin, y - 3);
  doc.setFontSize(6.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(150, 150, 150);
  doc.text(
    `Generated: ${printDate} · WestProp VMS SHEQ Module · Record ref: ${record.id?.slice(-8) ?? ""}`,
    pageW / 2,
    y,
    { align: "center" },
  );

  // ── Download ───────────────────────────────────────────────────────────
  const safeName = record.workerName.replace(/[^a-zA-Z0-9]/g, "_");
  const dateStr = (record.returnDate ?? new Date().toISOString().slice(0, 10));
  doc.save(`PPE_Return_Slip_${safeName}_${dateStr}.pdf`);
}
